import store from "../store";
import BaseHttpService from "./https.service";

const isLocal = window.location.href.indexOf('localhost') >= 0;
const managerBaseUrl = isLocal ? 'http://localhost:5001/manager' : '/manager';

class AdminHttpService extends BaseHttpService {
  constructor() {
    super(managerBaseUrl);
  }

  // Permission Management
  async getAllPermissions() {
    return await this.get("/permission/all");
  }

  async getPermission(permissionId) {
    return await this.get("/permission/item", { permissionId });
  }

  async addPermission(permissionData) {
    return await this.post("/permission/add", permissionData);
  }

  async editPermission(id, data) {
    return await this.post("/permission/edit", { id, data });
  }

  async deletePermission(id) {
    return await this.post("/permission/delete", { id });
  }

  // Group Mapping Management
  async getAllGroupMappings() {
    const response = await this.get("/group-mappings");
    return response;
  }

  async getGroupMapping(mappingId) {
    return await this.get(`/group-mappings/${mappingId}`);
  }

  async addGroupMapping(mappingData) {
    
    // Convert frontend data structure to backend expectations
    const data = {
      group_name: mappingData.group_name || mappingData.groupName,
      role_id: mappingData.role_id || mappingData.roleId,
      description: mappingData.description || ''
    };
    
    // Debug: Log the data being sent to backend
    console.log('🔍 [AdminService] Sending group mapping data:', data);
    
    return await this.post("/group-mappings", data);
  }

  async editGroupMapping(mappingId, data) {
    
    // Convert frontend data structure to backend expectations
    const mappingData = {
      group_name: data.group_name || data.groupName,
      role_id: data.role_id || data.roleId,
      description: data.description || ''
    };
    
    return await this.put(`/group-mappings/${mappingId}`, mappingData);
  }

  async deleteGroupMapping(mappingId) {
    return await this.delete(`/group-mappings/${mappingId}`);
  }

  async getGroupMappingStatus() {
    return await this.get("/group-mappings/status");
  }

  async refreshGroupMappings() {
    return await this.post("/group-mappings/refresh");
  }

  // Group App Mapping Management
  async getAllGroupAppMappings() {
    const response = await this.get("/group-app-mappings");
    return response;
  }

  async getGroupAppMapping(mappingId) {
    return await this.get(`/group-app-mappings/${mappingId}`);
  }

  async getGroupAppMappingByGroupName(groupName) {
    return await this.get(`/group-app-mappings/group/${groupName}`);
  }

  async addGroupAppMapping(mappingData) {
    
    // Convert frontend data structure to backend expectations
    const data = {
      group_name: mappingData.group_name || mappingData.groupName,
      app_access: mappingData.app_access || mappingData.appAccess || [],
      has_all_access: mappingData.has_all_access || mappingData.hasAllAccess || false,
      description: mappingData.description || ''
    };
    
    return await this.post("/group-app-mappings", data);
  }

  async addAppAccessToGroup(groupName, appAccess) {
    // Convert camelCase to snake_case for backend
    const data = {
      app_access: {
        app_id: appAccess.appId,
        app_name: appAccess.appName
        // No access_type needed - if it's in the array, they have access
      }
    };
    return await this.post(`/group-app-mappings/${groupName}/apps`, data);
  }

  async editGroupAppMapping(mappingId, data) {
    
    // Convert frontend data structure to backend expectations
    const mappingData = {
      group_name: data.group_name || data.groupName,
      app_access: data.app_access || data.appAccess || [],
      has_all_access: data.has_all_access || data.hasAllAccess || false,
      description: data.description || ''
    };
    
    return await this.put(`/group-app-mappings/${mappingId}`, mappingData);
  }

  async removeAppAccessFromGroup(groupName, appId) {
    return await this.delete(`/group-app-mappings/${groupName}/apps/${appId}`);
  }

  async deleteGroupAppMapping(mappingId) {
    return await this.delete(`/group-app-mappings/${mappingId}`);
  }

  async getGroupAppMappingStatus() {
    return await this.get("/group-app-mappings/status");
  }

  async refreshGroupAppMappings() {
    return await this.post("/group-app-mappings/refresh");
  }

  async getAppAccessDetails() {
    return await this.get("/group-app-mappings/access-details");
  }

  async getMyAccessibleApps() {
    return await this.get("/group-app-mappings/my-apps");
  }

  // Cache Management
  async getCacheStatus() {
    return await this.get("/permission/cache/status");
  }

  async refreshCache() {
    return await this.post("/permission/refresh-all");
  }

  // User Authorization Info
  async getUserAuthInfo() {
    return await this.get("/auth/user-info");
  }

  // Get all available apps
  async getAllApps() {
    const response = await this.get("/automation/get");
    return response;
  }

  // Email Mapping Management
  async getAllEmailMappings() {
    const response = await this.get("/email-mappings");
    return response;
  }

  async getEmailMapping(mappingId) {
    return await this.get(`/email-mappings/${mappingId}`);
  }

  async addEmailMapping(mappingData) {
    const data = {
      email: mappingData.email.toLowerCase().trim(),
      role_id: mappingData.role_id,
      role_name: mappingData.role_name, // Keep for backward compatibility
      description: mappingData.description || ''
    };
    
    return await this.post("/email-mappings/add", data);
  }

  async updateEmailMapping(mappingId, mappingData) {
    const data = {
      email: mappingData.email.toLowerCase().trim(),
      role_id: mappingData.role_id,
      role_name: mappingData.role_name, // Keep for backward compatibility
      description: mappingData.description || ''
    };
    
    return await this.put(`/email-mappings/${mappingId}`, data);
  }

  async deleteEmailMapping(mappingId) {
    return await this.delete(`/email-mappings/${mappingId}`);
  }

  async getEmailMappingStatus() {
    return await this.get("/email-mappings/status");
  }

  async refreshEmailMappings() {
    return await this.post("/email-mappings/refresh");
  }

  // Email App Access Management
  async getAllEmailAppAccessMappings() {
    const response = await this.get("/email-app-access-mappings");
    return response;
  }

  async getEmailAppAccessMapping(mappingId) {
    return await this.get(`/email-app-access-mappings/${mappingId}`);
  }

  async getEmailAppAccessMappingByEmail(email) {
    return await this.get(`/email-app-access-mappings/email/${encodeURIComponent(email)}`);
  }

  async addEmailAppAccessMapping(mappingData) {
    const data = {
      email: mappingData.email.toLowerCase().trim(),
      has_all_access: mappingData.has_all_access || false,
      app_access: mappingData.app_access || [],
      description: mappingData.description || ''
    };
    
    return await this.post("/email-app-access-mappings", data);
  }

  async updateEmailAppAccessMapping(mappingId, mappingData) {
    const data = {
      email: mappingData.email.toLowerCase().trim(),
      has_all_access: mappingData.has_all_access || false,
      app_access: mappingData.app_access || [],
      description: mappingData.description || ''
    };
    
    return await this.put(`/email-app-access-mappings/${mappingId}`, data);
  }

  async deleteEmailAppAccessMapping(mappingId) {
    return await this.delete(`/email-app-access-mappings/${mappingId}`);
  }

  async getEmailAppAccessMappingStatus() {
    return await this.get("/email-app-access-mappings/status");
  }

  async refreshEmailAppAccessMappings() {
    return await this.post("/email-app-access-mappings/refresh");
  }
}

export const adminService = new AdminHttpService();